import { useEffect, useState } from 'react';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './config';

/**
 * The client writes only `pending` — the rules refuse any other status, and
 * only the admin callable moves it. A request that could set its own status
 * would be a self-approval.
 *
 * I3 — this is also the RESUBMIT path for a rejected requester: setDoc
 * without `merge` is a full overwrite either way, and Firestore treats it as
 * a create when no document exists yet and an update when one does. The
 * rules now permit exactly one update transition on this document —
 * rejected -> pending (firestore.rules:189-...) — so a second call here
 * after a rejection lands as that transition instead of the create rule
 * refusing it outright.
 */
export async function submitMuqriRequest(uid: string, displayName: string, note: string): Promise<void> {
  await setDoc(doc(db, 'muqriRequests', uid), {
    displayName,
    note,
    status: 'pending',
    createdAt: serverTimestamp(),
  });
}

export type MuqriRequestStatus = 'pending' | 'approved' | 'rejected' | null;

/**
 * I3 — RequestMuqriScreen used to start at 'idle' on every mount, with no
 * memory of a request already sent. A returning pending/approved/rejected
 * requester saw a blank form, and submitting it again hit the rules'
 * create-only refusal (muqriRequests/{uid} already exists) or — for a
 * rejected requester before this fix's rules change — an unconditional
 * update refusal, and either was shown as `muqriRequest.failed`: "the
 * request could not be sent", which is false. It was sent; it is pending
 * (or approved, or rejected). Live, the same reasoning as useMuqriRole in
 * roles.ts: an admin can decide while this screen is open.
 *
 * `null` covers both "no request has ever been sent" and "signed out" —
 * both read the same way to this screen: show the form.
 */
export function useMuqriRequestStatus(uid: string | null): { status: MuqriRequestStatus; loading: boolean } {
  const [state, setState] = useState<{ status: MuqriRequestStatus; loading: boolean }>({
    status: null, loading: true,
  });
  useEffect(() => {
    setState({ status: null, loading: true });
    if (!uid) { setState({ status: null, loading: false }); return; }
    return onSnapshot(
      doc(db, 'muqriRequests', uid),
      (snap) => {
        const raw = snap.data()?.status;
        const status = raw === 'pending' || raw === 'approved' || raw === 'rejected' ? raw : null;
        setState({ status, loading: false });
      },
      () => setState({ status: null, loading: false })
    );
  }, [uid]);
  return state;
}
